import { PromiseUtils } from "../../../../../../Global/Base/Promises/PromiseUtils";
import { Localization } from "../../../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../../../UI.BaseControls/Checkbox";
import { Control } from "../../../../../UI.BaseControls/Control";
import { DateTimeEdit } from "../../../../../UI.BaseControls/DateTimeEdit";
import { TimeFormat } from "../../../../../UI.BaseControls/TimeEdit";
import { createElement, registerCustomElement } from "../../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IOperation, IReplaceFilterItem, IReplaceItemData } from "../FilterInterfaces";
import { dateTimeFilterRecreateFunction } from "../FilterShared";
import { IStructureExtension } from "../IStructureExtension";
import { IWithInclude } from "../IWithInclude";

export class DateTimeWithInclude extends DateTimeEdit implements IStructureExtension, IWithInclude, IReplaceFilterItem {
    private _include: Checkbox;
    private _desc: HTMLElement;
    private _subOperationID: string = null;

    //   private _includeChangeListener: EventListener;
    //   private _dateEditKeyUpListener: EventListener;

    public get subOperationID(): string {
        return this._subOperationID;
    }

    public set subOperationID(value: string) {
        if ((value == "day") && (value != this._subOperationID)) {
            this._subOperationID = value;
            this._timeEdit.controlValue = null;
            this._updateOriginalValue();
        } else {
            this._subOperationID = value;
            this._updateOriginalValue();
        }
    }

    public get include(): boolean {
        return this._include.visible && this._include.checked;
    }

    public set include(v: boolean) {
        this._include.checked = v;
        this._updateOriginalValue();
        this.callEvent("change");
    }

    public get includeVisible(): boolean {
        return this._desc.parentElement.style.display != "none";
    }

    public set includeVisible(v: boolean) {
        this._desc.parentElement.style.display = v ? "" : "none";
    }

    public applyDisplayFormat(displayFormat: string): void {
        if (["D", "d", "M", "m"].some(x => x === displayFormat)) {
            this._timeEdit.visible = false;
            this._dateEdit.classList.add("OnlyDate");
        }
        if (displayFormat == "t")
            this._dateEdit.visible = false;

        this._timeEdit.setAttribute("timeformat", displayFormat);
    }

    protected override _constructor(): void {
        super._constructor();

        const cont = createElement({ classes: "check-cont" });
        this._include = new Checkbox();
        this._include.required = true;
        this._desc = createElement({ classes: "description" });

        cont.style.display = "none";

        this._include.standardAction = true;

        Localization.addLocData(this._desc, "GUI-603192"); //Včetně

        this._include.addEventListener("change", () => this.include = this._include.value);

        this._dateEdit.registerEventListener("keyup", (e) => {
            this.callEvent("keyup", { original: e });
        });

        cont.appendChild(this._include);
        cont.appendChild(this._desc);

        this.appendChild(cont);

        this._timeEdit.setAttribute("timeformat", "t");

        this.classList.add("DateTimeEditWithInclude");
    }

    protected override connectedCallback(): void {
        super.connectedCallback();

        Control.unregisterEventListener(this._eventGroup, this._innerElement, "focusout");
        Control.registerEventListener(this._eventGroup, this._innerElement, "focusout", () => {
            if (!this.isValid) // stav nevalidnosti nektere komponenty se nebude prepisovat stavem, ze jedno pole neni vyplneno
                return;

            if (!this._dateEdit.controlValue && this._timeEdit.controlValue) {
                // je potřeba počka, až se projeví ztráta focusu
                PromiseUtils.delay(0).then(() => {
                    // pokud ani jedna subkomponenta nemá focus, tak se validuje, jestli jsou obě nevyplněné nebo obě vyplněné
                    if (!this._timeEdit.isFocused && !this._dateEdit.isFocused &&
                        (!this._dateEdit.controlValue && this._timeEdit.controlValue))
                        this.setInvalid("VAL-615396"); // Všechna pole musí být vyplněna
                });
            }
        });
    }

    protected override _updateOriginalValue(): boolean {
        const date = this._dateEdit.controlValue;
        const time = this._timeEdit.visible == false ? null : this._timeEdit.controlValue;
        const oldOriginalValue = this._originalValue;

        // az kdyz jsou vyplnena obe pole, tak se sklada vysledna hodnota DateTimeEditu
        if (date) {
            this._originalValue = new Date(date);

            if (time) {
                if (this.subOperationID != null)
                    this.subOperationID = null;
                this._originalValue.setHours(time.getHours());
                this._originalValue.setMinutes(time.getMinutes());
                this._originalValue.setSeconds(time.getSeconds());
                this._originalValue.setMilliseconds(time.getMilliseconds());
                this.updateRoundedTime();
            } else {
                if (this.subOperationID != "day")
                    this.subOperationID = "day";
                this.setDefaultTime();
            }
        } else {
            this._originalValue = null;
        }

        let changed = false;
        if ((oldOriginalValue == null && this._originalValue != null) || (oldOriginalValue != null && this._originalValue == null)) changed = true;
        if (oldOriginalValue != null && this._originalValue != null && oldOriginalValue.valueOf() != this._originalValue.valueOf()) changed = true;

        // zmena hodnoty se ohlasuje az kdyz jsou obe casti vyplnene nebo obe prazdne
        return ((date != null) || (time == null && date == null)) && changed;
    }

    private setDefaultTime(): void {
        if (this.includeVisible && ((this.include && this._defaultValue) || (!this.include && !this._defaultValue))) {
            this._originalValue.setHours(23);
            this._originalValue.setMinutes(59);
            this._originalValue.setSeconds(59);
            this._originalValue.setMilliseconds(59);
        } else {
            this._originalValue.setHours(0);
            this._originalValue.setMinutes(0);
            this._originalValue.setSeconds(0);
            this._originalValue.setMilliseconds(0);
        }
    }

    /**
     * Pokud je zadaný čas, ale format zobrazuje jen minuty nebo sekundy tak se musí upravit
     * časová složka podle vybraného filtru a včetně
     * */
    private updateRoundedTime(): void {
        if (this.includeVisible && ((this.include && this._defaultValue) || (!this.include && !this._defaultValue))) {
            this._originalValue.setMilliseconds(59);
            if (this._timeEdit.timeFormat == TimeFormat.tfMin)
                this._originalValue.setSeconds(59);
        } else {
            this._originalValue.setMilliseconds(0);
            if (this._timeEdit.timeFormat == TimeFormat.tfMin)
                this._originalValue.setSeconds(0);
        }
    }

    public override validate(forced: boolean = false): boolean {
        this._dateEdit.validate();

        if (this._timeEdit.controlValue)
            this._timeEdit.validate();
        if (forced && (!this._dateEdit.controlValue && this._timeEdit.controlValue))
            this.setInvalid("VAL-615396"); // Všechna pole musí být vyplněna
        return this.isValid;
    }

    //#region Dispose
    public override dispose(): void {
        //this._include.removeEventListener('change', this._includeChangeListener); //TODO: IFRAME - vola se už při skrytí popup menu a nenaváže se to znovu při zobrazení
        //this._dateEdit.removeEventListener('keyup', this._dateEditKeyUpListener); //TODO: IFRAME - vola se už při skrytí popup menu a nenaváže se to znovu při zobrazení
        super.dispose();
    }

    public recreateFilter(operation: IOperation): IReplaceItemData[] {
        return dateTimeFilterRecreateFunction(this._dateEdit.userValue, operation);
    }
}

registerCustomElement("tsc-DateTimeWithInclude", DateTimeWithInclude);
